// Canonicalization of API URLs into stable, fingerprintable routes.
//
// A Sentry issue for an API failure must group every equivalent failure together,
// so the route it is keyed on can only contain the parts of the URL that are the
// same for every caller: the path, with record identifiers replaced by a
// placeholder. Host, query and fragment are dropped — they carry deployment and
// per-request data (search terms, filter values, page numbers) that would explode
// one endpoint into thousands of issues.

import { MAX_TAG_VALUE_LENGTH } from './sanitize.js';

// Base used when the raw URL is relative and no axios `baseURL` was given. Never
// reached by the SDK client (its URLs are absolute), so the host is irrelevant —
// only `pathname` is read back out.
const FALLBACK_BASE = 'http://api.invalid';

const NUMERIC_SEGMENT = /^\d+$/;
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX_SEGMENT = /^[0-9a-f]{32}$/i;

// The generated OpenAPI schema only uses `{id}` and `{order_id}` as dynamic path
// parameters, and a URL alone cannot tell the two apart. Collapsing both to
// `{id}` keeps grouping stable — two different endpoints never collide, because
// the surrounding static segments still differ.
export const ROUTE_ID_PLACEHOLDER = '{id}';

const isIdentifierSegment = (segment: string): boolean =>
  NUMERIC_SEGMENT.test(segment) || UUID_SEGMENT.test(segment) || HEX_SEGMENT.test(segment);

/**
 * Path of `rawUrl`, with the origin, query string and fragment removed.
 * Accepts absolute and relative URLs; `baseUrl` is the axios `baseURL`, used to
 * resolve relative ones.
 */
export const toUrlPath = (rawUrl: string, baseUrl?: string): string => {
  for (const base of [baseUrl, FALLBACK_BASE]) {
    if (!base) {
      continue;
    }
    try {
      return new URL(rawUrl, base).pathname;
    } catch {
      // Malformed base or URL — fall through to the next candidate.
    }
  }

  // Last resort for input `URL` cannot parse at all: strip fragment then query.
  return rawUrl.split('#')[0].split('?')[0];
};

/**
 * Canonical route of `rawUrl`: its path with every numeric/UUID identifier
 * segment replaced by `{id}`. Static action segments (`.../end/`,
 * `.../document-context/`) are preserved, so distinct endpoints stay distinct.
 */
export const canonicalizeRoute = (rawUrl: string, baseUrl?: string): string => {
  const route = toUrlPath(rawUrl, baseUrl)
    .split('/')
    .map((segment) => (isIdentifierSegment(segment) ? ROUTE_ID_PLACEHOLDER : segment))
    .join('/');

  // The route is written to a tag, so cut it deterministically rather than letting
  // Sentry truncate it wherever its own limit falls.
  return route.length > MAX_TAG_VALUE_LENGTH ? route.slice(0, MAX_TAG_VALUE_LENGTH) : route;
};
